import { damagePlayer } from './player';

// Frame layout (96×66 per frame, 11 cols × 2 rows):
// Row 0 (0–10):  Walk 01–05, Stand/Idle 01–02, AttackRaise 01–02, AttackThrow, Hurt
// Row 1 (11–21): Die 01–04, Hat 01–06, (empty)
const FRAMES = {
  walkStart:   0,
  walkEnd:     4,
  idleStart:   5,
  fistStart:   5,
  fistEnd:     7,
  raiseStart:  7,
  raiseEnd:    8,
  throw:       9,
  hurt:        10,
  dieStart:    11,
  dieEnd:      14,
  hat:         15,
};

const DETECT_RANGE    = 220;
const MELEE_RANGE     = 72;
const WALK_SPEED      = 70;
const LUNGE_SPEED     = 220;
const RANGED_COOLDOWN = 4000;
const FIST_INTERVAL   = 5000;
const JAB_SPEED       = 110;

// Frame is 116px wide with hat centered at x=58 (frame center).
// flipX mirrors cleanly — no x compensation needed.

function turnCroc(croc, faceRight) {
  if (croc.flipX === faceRight) return;
  croc.flipX = faceRight;
}

export function createCroc(scene) {
  scene.anims.create({
    key: 'crocWalk',
    frames: scene.anims.generateFrameNumbers('croc', { start: FRAMES.walkStart, end: FRAMES.walkEnd }),
    frameRate: 8,
    repeat: -1,
  });
  scene.anims.create({
    key: 'crocFist',
    frames: scene.anims.generateFrameNumbers('croc', { start: FRAMES.fistStart, end: FRAMES.fistEnd }),
    frameRate: 3,
    repeat: 2,
  });
  scene.anims.create({
    key: 'crocRaise',
    frames: scene.anims.generateFrameNumbers('croc', { start: FRAMES.raiseStart, end: FRAMES.raiseEnd }),
    frameRate: 4,
    repeat: 0,
  });
  scene.anims.create({
    key: 'crocDie',
    frames: scene.anims.generateFrameNumbers('croc', { start: FRAMES.dieStart, end: FRAMES.dieEnd }),
    frameRate: 5,
    repeat: 0,
  });
  scene.anims.create({
    key: 'crocHatFall',
    frames: scene.anims.generateFrameNumbers('croc', { start: FRAMES.hat, end: FRAMES.hat + 3 }),
    frameRate: 15,
    repeat: 0,
  });

  const croc = scene.physics.add.sprite(860, 448, 'croc', FRAMES.idleStart);
  croc.setOrigin(0.5, 0.5);
  croc.setCollideWorldBounds(true);
  croc.body.setSize(40, 56);
  croc.body.setOffset(54, 10);
  croc.setDepth(5);

  croc.health       = 5;
  croc.isDying      = false;
  croc.hurtTimer    = 0;
  croc.state        = 'patrol';
  croc.patrolLeft   = 820;
  croc.patrolRight  = 1068;
  croc.direction    = -1;
  croc.flipX        = false;  // start facing left
  croc.rangedTimer  = RANGED_COOLDOWN;
  croc.fistTimer    = FIST_INTERVAL + Math.random() * 3000;
  croc.attackLocked = false;

  croc.hit = (_facing) => hitCroc(scene, croc);

  scene.physics.add.collider(croc, scene.environmentLayer);

  scene.physics.add.overlap(scene.player, croc, () => {
    if (croc.isDying) return;
    const alreadyInvincible = scene.playerInvincible;
    damagePlayer(scene, 0.5);
    if (scene.player.isDead) return;
    if (alreadyInvincible) return;
    if (scene.player.isClimbing || scene.player.isPerched) return;
    const knockDir = scene.player.x < croc.x ? -1 : 1;
    scene.player.setVelocityX(knockDir * 200);
    scene.player.knockbackTimer = 350;
  }, null, scene);

  scene.croc = croc;
}

function hitCroc(scene, croc) {
  if (croc.isDying || croc.hurtTimer > 0) return;
  croc.health -= 1;
  croc.hurtTimer = 800;
  croc.attackLocked = true;
  croc.setVelocityX(0);
  croc.anims.stop();
  croc.setFrame(FRAMES.hurt);
  scene.tweens.add({
    targets: croc,
    alpha: 0.2,
    duration: 60,
    yoyo: true,
    repeat: 3,
    onComplete: () => croc?.active && croc.setAlpha(1),
  });
  if (croc.health <= 0) { killCroc(scene, croc); return; }
  scene.time.delayedCall(700, () => {
    if (croc?.active && !croc.isDying) {
      croc.attackLocked = false;
      croc.anims.play('crocWalk', true);
    }
  });
}

function killCroc(scene, croc) {
  if (croc.isDying) return;
  croc.isDying = true;
  croc.attackLocked = true;
  croc.setVelocity(0, 0);
  croc.body.setAllowGravity(false);
  croc.anims.play('crocDie');
  croc.once('animationcomplete-crocDie', () => {
    if (!croc?.active) return;
    const floorY = croc.body.bottom;
    const hat = scene.add.sprite(croc.x, floorY - 40, 'croc', FRAMES.hat);
    hat.setOrigin(0.5, 0.29);
    hat.setDepth(4);
    hat.anims.play('crocHatFall');
    hat.once('animationcomplete-crocHatFall', () => {
      hat.setFrame(FRAMES.hat);
      hat.y = floorY;
    });
    croc.destroy();
  });
}

function doLunge(scene, croc) {
  const dir = croc.flipX ? 1 : -1;
  croc.setFrame(FRAMES.throw);
  croc.setVelocityX(dir * LUNGE_SPEED);
  scene.time.delayedCall(500, () => {
    if (!croc?.active || croc.isDying) return;
    croc.setVelocityX(0);
    croc.attackLocked = false;
    croc.setFrame(FRAMES.idleStart);
  });
}

function doJab(scene, croc, dirTowardRat) {
  // Wind up (trident rising) → jab → hop back → recover
  croc.attackLocked = true;
  croc.setVelocityX(0);
  croc.anims.stop();
  croc.setFrame(FRAMES.raiseStart);           // frame 7: trident starts up
  scene.time.delayedCall(150, () => {
    if (!croc?.active || croc.isDying) return;
    croc.setFrame(FRAMES.raiseEnd);           // frame 8: trident fully raised
    scene.time.delayedCall(150, () => {
      if (!croc?.active || croc.isDying) return;
      croc.setFrame(FRAMES.throw);            // frame 9: jab
      croc.setVelocityX(dirTowardRat * JAB_SPEED);
      scene.time.delayedCall(180, () => {
        if (!croc?.active || croc.isDying) return;
        croc.setFrame(FRAMES.idleStart);
        croc.setVelocityX(-dirTowardRat * JAB_SPEED);
        scene.time.delayedCall(180, () => {
          if (!croc?.active || croc.isDying) return;
          croc.setVelocityX(0);
          scene.time.delayedCall(250, () => {
            if (!croc?.active || croc.isDying) return;
            croc.attackLocked = false;
          });
        });
      });
    });
  });
}

export function updateCroc(scene, delta) {
  const croc = scene.croc;
  if (!croc?.active || croc.isDying) return;

  if (croc.hurtTimer > 0) croc.hurtTimer -= delta;
  if (croc.attackLocked) return;

  const distX     = scene.player.x - croc.x;
  const absX      = Math.abs(distX);
  const absY      = Math.abs(scene.player.y - croc.y);
  const sameFloor = absY < 48;
  const engaged   = sameFloor && absX < DETECT_RANGE;

  // ── ENGAGED ────────────────────────────────────────────────
  if (engaged) {
    croc.state = 'engaged';
    croc.rangedTimer -= delta;

    // Melee range — dance/jab, always facing rat
    if (absX < MELEE_RANGE) {
      croc.flipX = distX > 0;
      doJab(scene, croc, Math.sign(distX));
      return;
    }

    // Lunge attack
    if (croc.rangedTimer <= 0) {
      croc.rangedTimer = RANGED_COOLDOWN;
      croc.attackLocked = true;
      croc.setVelocityX(0);
      turnCroc(croc, distX > 0);
      croc.anims.play('crocRaise');
      croc.once('animationcomplete-crocRaise', () => {
        if (!croc?.active || croc.isDying) return;
        doLunge(scene, croc);
      });
      return;
    }

    // Fist shake — the only vulnerability window, only while engaged
    croc.fistTimer -= delta;
    if (croc.fistTimer <= 0) {
      croc.fistTimer = FIST_INTERVAL + Math.random() * 3000;
      croc.attackLocked = true;
      croc.setVelocityX(0);
      croc.anims.stop();
      croc.setFrame(FRAMES.idleStart);
      scene.time.delayedCall(350, () => {
        if (!croc?.active || croc.isDying) return;
        croc.anims.play('crocFist');
        croc.once('animationcomplete-crocFist', () => {
          if (!croc?.active || croc.isDying) return;
          // Lower trident: step back down 7 → 6 → 5 before resuming
          scene.time.delayedCall(150, () => {
            if (!croc?.active || croc.isDying) return;
            croc.setFrame(6);
            scene.time.delayedCall(150, () => {
              if (!croc?.active || croc.isDying) return;
              croc.setFrame(FRAMES.idleStart);
              scene.time.delayedCall(200, () => {
                if (!croc?.active || croc.isDying) return;
                croc.attackLocked = false;
                croc.anims.play('crocWalk', true);
              });
            });
          });
        });
      });
      return;
    }

    // Stalk — slowly advance toward rat while facing it
    turnCroc(croc, distX > 0);
    croc.setVelocityX(Math.sign(distX) * WALK_SPEED * 0.5);
    croc.anims.play('crocWalk', true);
    return;
  }

  // ── PATROL ─────────────────────────────────────────────────
  croc.state = 'patrol';
  croc.rangedTimer = Math.max(croc.rangedTimer, RANGED_COOLDOWN * 0.5);
  croc.fistTimer = FIST_INTERVAL; // reset so fist doesn't fire immediately on next engagement

  // Walk patrol
  if (croc.x <= croc.patrolLeft  || croc.body.blocked.left)  croc.direction =  1;
  if (croc.x >= croc.patrolRight || croc.body.blocked.right) croc.direction = -1;
  croc.setVelocityX(croc.direction * WALK_SPEED);
  turnCroc(croc, croc.direction > 0);
  croc.anims.play('crocWalk', true);
}
